/* ═══════════════════════════════════════════════════════════════
   forecasting/_shared — shared types, styles, utilities, and the
   small generic UI primitives (Modal / SegmentedControl / ToggleChip)
   used across the forecasting page.

   These are forecasting-local on purpose: lifting them to a global
   design-system would force a wider refactor (shadcn / Tailwind) that
   the rest of the app hasn't adopted yet. Keep the surface tight and
   move them once the broader migration lands.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useRef } from 'react';
import { PROJECT_PALETTE } from '../../lib/palette';

// ─── Types ───────────────────────────────────────────────────────────

export type GroupBy = 'department' | 'team' | 'project' | 'none';

/** People-scope: which set of team members to show. Replaces the old
 *  binary "mine only" toggle.
 *   - mine: only the current user
 *   - team: members of teams the current user leads (team_assignments.team_role='lead')
 *   - pm:   anyone who appears on projects where the current user is pm_id / pic_id
 *   - all:  full firm (gated by forecasting.view_all)
 *  Default is computed at runtime from the user's role + data.
 */
export type Scope = 'mine' | 'team' | 'pm' | 'all';

/** Breakdown axis used inside Project view. The page renders one row per
 *  project; under each, sub-rows are grouped by:
 *   - individual:  one row per person on that project (editable)
 *   - team:        one row per team with members on that project
 *   - department:  one row per department with members on that project
 *  Team / department modes are analytical views — sub-rows are read-only
 *  because a click can't unambiguously route to a single member's cell.
 */
export type Breakdown = 'individual' | 'team' | 'department';

export interface ViewConfig {
  groupBy: GroupBy;
  scope: Scope;
  /** Only meaningful when `groupBy === 'project'`. Default 'individual' so
   *  the out-of-box experience matches the most common workflow. */
  projectBreakdown: Breakdown;
  selectedDepartmentIds: string[]; // empty = all
  selectedTeamIds: string[]; // empty = all
  selectedProjectIds: string[]; // empty = all
  showWeekend: boolean;
  showInactiveMembers: boolean;
}

export const DEFAULT_CONFIG: ViewConfig = {
  groupBy: 'department',
  scope: 'mine',
  projectBreakdown: 'individual',
  selectedDepartmentIds: [],
  selectedTeamIds: [],
  selectedProjectIds: [],
  showWeekend: false,
  showInactiveMembers: false,
};

export type MemberCapacity = {
  rawTarget: number; // contractual weekly hours (40 / 20 / custom)
  vacationHours: number; // sum of source_kind='vacation' across the week
  workedHours: number; // total minus vacation = actual project allocation
  availableHours: number; // rawTarget minus vacation = meaningful denominator
  totalHours: number; // workedHours + vacationHours
  isPartTime: boolean;
  color: string;
  label: string;
};

// ─── Color + date utilities ──────────────────────────────────────────

// PROJECT_PALETTE now lives in ../../lib/palette (single source for all
// data-swatch palettes) — re-exported here so existing imports of
// `PROJECT_PALETTE` from this module keep working unchanged.
export { PROJECT_PALETTE };

/** Stable hue per project — same project shows the same color across weeks. */
export function projectColor(projectId: string): string {
  let hash = 0;
  for (let i = 0; i < projectId.length; i++) {
    hash = (hash * 31 + projectId.charCodeAt(i)) | 0;
  }
  return PROJECT_PALETTE[Math.abs(hash) % PROJECT_PALETTE.length];
}

export const VACATION_PROJECT_LABEL = 'Vacation';
export const VACATION_COLOR = '#6b7280';

/** Today as yyyy-mm-dd in local time (forecast/timesheet semantics
 *  don't need a TZ — the user picks a workday, the wire stores civil-date). */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function formatShort(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][dt.getUTCMonth()]} ${dt.getUTCDate()}`;
}

export function formatLong(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return `${days[dt.getUTCDay()]}, ${formatShort(iso)}, ${y}`;
}

/** Whole-day delta between two yyyy-mm-dd dates. Negative if `a` > `b`. */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

// ─── Style constants ─────────────────────────────────────────────────

export const headerCell: React.CSSProperties = {
  padding: '10px 14px',
  background: 'var(--sf)',
  borderBottom: '1px solid var(--bd)',
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

export const navBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--tx)',
  fontSize: 14,
  fontWeight: 700,
  padding: '6px 8px',
  borderRadius: 4,
  cursor: 'pointer',
};

export const chipBtn: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid var(--bd)',
  background: 'var(--sa)',
  color: 'var(--tx)',
  fontSize: 11,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  transition: 'all .12s',
};

export const fieldBlock: React.CSSProperties = {
  display: 'block',
  marginBottom: 10,
};
export const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--tm)',
  marginBottom: 4,
};
export const textInput: React.CSSProperties = {
  padding: '7px 10px',
  borderRadius: 5,
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 13,
  width: '100%',
};
export const btnPrimary: React.CSSProperties = {
  padding: '7px 14px',
  borderRadius: 6,
  border: 'none',
  background: 'var(--ac)',
  color: '#fff',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
export const btnGhost: React.CSSProperties = {
  padding: '7px 14px',
  borderRadius: 6,
  border: '1px solid var(--bd)',
  background: 'var(--sf)',
  color: 'var(--tx)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
export const btnDanger: React.CSSProperties = {
  padding: '7px 14px',
  borderRadius: 6,
  border: '1px solid var(--dg)',
  background: 'transparent',
  color: 'var(--dg)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
export const miniIconBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--tm)',
  fontSize: 12,
  padding: '2px 4px',
  cursor: 'pointer',
  borderRadius: 3,
  flexShrink: 0,
};
export const viewsSectionHead: React.CSSProperties = {
  padding: '6px 12px 2px',
  fontSize: 9,
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  borderTop: '1px solid var(--bd)',
};

// ─── Modal — focus-trapped, Esc-closing dialog ───────────────────────

export function Modal({
  children,
  onClose,
  title,
}: {
  children: React.ReactNode;
  onClose: () => void;
  title: string;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<Element | null>(null);

  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    const root = dialogRef.current;
    if (!root) return;
    const focusables = root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    (focusables[0] || root).focus();
    return () => {
      const prev = previouslyFocused.current as HTMLElement | null;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--bd)',
          borderRadius: 10,
          padding: 20,
          minWidth: 360,
          maxWidth: 480,
          boxShadow: '0 16px 40px rgba(0,0,0,.24)',
          outline: 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close dialog"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--tm)',
              fontSize: 18,
              cursor: 'pointer',
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── SegmentedControl + ToggleChip ───────────────────────────────────

export function SegmentedControl({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--tm)',
          textTransform: 'uppercase',
          letterSpacing: '.05em',
        }}
      >
        {label}
      </span>
      <div style={{ display: 'flex', background: 'var(--sa)', borderRadius: 6, padding: 2 }}>
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              onClick={() => onChange(o.value)}
              aria-pressed={active}
              style={{
                padding: '4px 10px',
                borderRadius: 4,
                background: active ? 'var(--sf)' : 'transparent',
                color: active ? 'var(--tx)' : 'var(--tm)',
                fontSize: 11,
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
                transition: 'all .12s',
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ToggleChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      style={{
        ...chipBtn,
        background: active ? 'color-mix(in srgb, var(--ac) 12%, transparent)' : 'var(--sa)',
        color: active ? 'var(--ac)' : 'var(--tx)',
        fontWeight: active ? 700 : 500,
      }}
    >
      {label}
    </button>
  );
}
